import type { ApiClient } from './client';
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type {
  GovernedSettings,
  GovernedStatus,
  GovernedTenant,
  LedgerCustody,
  LedgerEntries,
  LedgerEntryKind,
  LedgerEvidenceExport,
  LedgerInclusionProof,
  LedgerRangeVerification,
  OversightAiPause,
  OversightChannels,
  OversightEvent,
  OversightEventPath,
  OversightEvents,
  OversightGuardianSignals,
  OversightGuards,
  OversightHeldDecision,
  OversightHeldDecisionResult,
  OversightHeldList,
  OversightHuman,
  OversightKillSwitch,
  OversightModeled,
  OversightPatterns,
  OversightReviewers,
  OversightThread,
  OversightTraces,
  SecurityAudit,
} from './oversight-types';

const A = AITHORA_PREFIX;

// ── /api/aithora/oversight (src/aithora/v3/oversight.ts) ────────────────────
// Scope comes from the session: a Facility Admin is pinned to their own building.

/** GET /oversight/events?from=&to=&limit=: the unified feed, oldest first (default: last 15 min, max 24 h). */
export const oversightEvents = route<OversightEvents, { from?: string; to?: string; limit?: number }>()('GET', `${A}/oversight/events`, { honest: true });
/** The SSE path of the same feed (event: oversight.<type>). */
export const OVERSIGHT_EVENTS_STREAM_PATH = `${A}/oversight/events/stream` as const;
/**
 * GET /oversight/events/stream?since= (SSE, event: oversight.<type>): read with fetch so the
 * session's Authorization header goes with it (EventSource cannot send one). Calls onEvent
 * for every event and resolves when the server ends the stream, with the retry it asked for.
 * `limit` is passed through for callers that page; the server ignores it.
 */
export async function oversightEventStream(
  client: ApiClient,
  opts: { since: number; limit?: number; signal?: AbortSignal; onEvent: (e: OversightEvent) => void },
): Promise<{ retryMs: number | null }> {
  const qs = new URLSearchParams({ since: String(Math.trunc(opts.since)) });
  if (opts.limit) qs.set('limit', String(opts.limit));
  const res = await client.raw(`${OVERSIGHT_EVENTS_STREAM_PATH}?${qs}`, { headers: { Accept: 'text/event-stream' }, signal: opts.signal });
  if (!res.ok) {
    const err = new Error(`The live feed did not open (${res.status}).`) as Error & { status: number; requestId: string | null };
    err.status = res.status;
    err.requestId = res.headers.get('x-request-id');
    throw err;
  }
  let retryMs: number | null = null;
  const take = (block: string) => {
    const retry = /^retry: *(\d+)$/m.exec(block)?.[1];
    if (retry) retryMs = Number(retry);
    const ev = /^event: *(.+)$/m.exec(block)?.[1]?.trim();
    const data = block.split('\n').filter((l) => l.startsWith('data:')).map((l) => l.slice(5).replace(/^ /, '')).join('\n');
    if (!data || (ev && !ev.startsWith('oversight.'))) return;
    try {
      opts.onEvent(JSON.parse(data) as OversightEvent);
    } catch {
      /* a keepalive or a malformed line: skip it */
    }
  };
  const body = res.body;
  if (!body || typeof body.getReader !== 'function') {
    for (const block of (await res.text()).split(/\n\n+/)) take(block);
    return { retryMs };
  }
  const reader = body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true }).replace(/\r\n/g, '\n');
    let i: number;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      take(buf.slice(0, i));
      buf = buf.slice(i + 2);
    }
  }
  if (buf.trim()) take(buf);
  return { retryMs };
}

/** GET /oversight/events/:id/path: the gate stages, the rule that fired, the raw event and its ledger hash. */
export const oversightEventPath = route<OversightEventPath>()('GET', `${A}/oversight/events/:id/path`, { honest: true });
/** GET /oversight/held: what waits for a person. */
export const oversightHeld = route<OversightHeldList, { limit?: number }>()('GET', `${A}/oversight/held`, { honest: true });
/** POST /oversight/held/:id/decision {decision, reason?}: sealed to the ledger. */
export const oversightHeldDecide = route<OversightHeldDecisionResult, void, { decision: OversightHeldDecision; reason?: string; confirm: true }>()(
  'POST', `${A}/oversight/held/:id/decision`, { honest: true });
/** GET /oversight/human?window=24h: time to a person, overrule rate, reviewers. */
export const oversightHuman = route<OversightHuman, { window?: string }>()('GET', `${A}/oversight/human`, { honest: true });
/** GET /oversight/reviewers?window=24h: who decided what, and how fast. */
export const oversightReviewers = route<OversightReviewers, { window?: string }>()('GET', `${A}/oversight/reviewers`, { honest: true });
/** GET /oversight/patterns: repeated scam blocks for one resident. */
export const oversightPatterns = route<OversightPatterns>()('GET', `${A}/oversight/patterns`, { honest: true });
/** GET /oversight/modeled: before it trips, and the usual rate for this hour (both MODELED). */
export const oversightModeled = route<OversightModeled>()('GET', `${A}/oversight/modeled`, { honest: true });
/** POST /oversight/patterns/freeze-contact {residentId, contact?, reason}. */
export const oversightFreezeContact = route<{ real: boolean; freezeId: string; enforced: boolean; reason: string }, void, { residentId: string; contact?: string; reason: string; confirm: true }>()(
  'POST', `${A}/oversight/patterns/freeze-contact`, { honest: true });
/** POST /oversight/patterns/alert-family {residentId, reason?}. */
export const oversightAlertFamily = route<{ real: boolean; notified: number; reason: string | null }, void, { residentId: string; reason?: string; confirm: true }>()(
  'POST', `${A}/oversight/patterns/alert-family`, { honest: true });
/** GET /facilities/:id/ai-pause */
export const aiPauseState = route<OversightAiPause>()('GET', `${A}/facilities/:id/ai-pause`, { honest: true });
/** POST /facilities/:id/ai-pause {paused, reason?}: sealed, alerts Aithora, a pause pages on-call. */
export const aiPauseSet = route<OversightAiPause, void, { paused: boolean; reason?: string; confirm: true }>()('POST', `${A}/facilities/:id/ai-pause`, { honest: true });

// ── Truth Ledger (same file) ────────────────────────────────────────────────

/** POST /ledger/verify {scope?, from, to}: recompute every hash in the range. */
export const ledgerVerify = route<LedgerRangeVerification, void, { scope?: string; from: string; to: string }>()('POST', `${A}/ledger/verify`, { honest: true });
/** GET /ledger/entries/:id/proof: one entry, provable without the whole ledger. */
export const ledgerProof = route<LedgerInclusionProof>()('GET', `${A}/ledger/entries/:id/proof`, { honest: true });
/** POST /ledger/export {range, scope, reason, recipient}: a signed evidence file, written to custody. */
export const ledgerExport = route<LedgerEvidenceExport, void, { range: { from: string; to: string }; scope?: string; reason: string; recipient: string; confirm: true }>()(
  'POST', `${A}/ledger/export`, { honest: true });
/** GET /ledger/custody?scope=&limit=: who verified, exported or issued a proof. */
export const ledgerCustody = route<LedgerCustody, { scope?: string; limit?: number }>()('GET', `${A}/ledger/custody`, { honest: true });

/** GET /guardian/signals?limit=: Guardian cases with their sealed steps (the Truth Ledger strip). */
export const oversightGuardianSignals = route<OversightGuardianSignals, { open?: string; limit?: number }>()('GET', `${A}/guardian/signals`, { honest: true });

// ── /api (src/server.ts) ────────────────────────────────────────────────────

/** GET /api/kill: this tenant's kill switches. */
export const killSwitches = route<{ killSwitches: OversightKillSwitch[] }>()('GET', '/api/kill');
/** POST /api/kill {scope, reason}: stop. Anyone responsible may stop. */
export const killActivate = route<{ success: boolean; killSwitch: OversightKillSwitch }, void, { scope: 'global'; reason: string; confirm: true }>()('POST', '/api/kill');
/** POST /api/kill/deactivate {killSwitchId | all}: restart. The owner only (the server refuses everyone else). */
export const killDeactivate = route<{ success: boolean }, void, { killSwitchId?: string; all?: true; confirm: true }>()('POST', '/api/kill/deactivate');
/** GET /api/audit?severity=&limit=: security events for this tenant. */
export const securityAudit = route<SecurityAudit, { type?: string; severity?: string; limit?: number }>()('GET', '/api/audit');
/** GET /api/tenants: the governed systems this caller may see. */
export const governedTenants = route<{ tenants: GovernedTenant[] }>()('GET', '/api/tenants');
/** POST /api/tenants {name}: a new governed system (it starts staged). */
export const governedTenantCreate = route<{ success: boolean; tenant: GovernedTenant }, void, { name: string; confirm: true }>()('POST', '/api/tenants');
/** POST /api/tenants/:id/pause: back to staged, the gate stops being presented as enforcing. Operator tier only. */
export const governedTenantPause = route<{ success: boolean; tenant: GovernedTenant }, void, Record<string, never>>()('POST', '/api/tenants/:id/pause');
/** POST /api/tenants/:id/promote: staged to live (enforcing). Operator tier only. */
export const governedTenantPromote = route<{ success: boolean; tenant: GovernedTenant }, void, Record<string, never>>()('POST', '/api/tenants/:id/promote');
/** GET /api/settings: the tenant's settings (the gate's default posture: failClosed). */
export const governedSettings = route<GovernedSettings>()('GET', '/api/settings');
/** GET /api/status: the caller's tenant (policies, kill switches, threats, ledger). */
export const governedStatus = route<GovernedStatus>()('GET', '/api/status');

// ── Summary figures and the ledger list (served, 2026-09-30) ──

/** GET /ledger/entries?scope=&kind=&q=&limit=: sealed entries, newest first, with head and counts. */
export const ledgerEntries = route<LedgerEntries, { scope?: string; kind?: LedgerEntryKind | 'People'; q?: string; limit?: number }>()('GET', `${A}/ledger/entries`, { honest: true });
/** GET /oversight/guards: each guard's load against its trip line. */
export const oversightGuards = route<OversightGuards>()('GET', `${A}/oversight/guards`, { honest: true });
/** GET /oversight/traces?points=24: the Summary's five counts over time. */
export const oversightTraces = route<OversightTraces, { points?: number }>()('GET', `${A}/oversight/traces`, { honest: true });
/** GET /oversight/thread: the newest signal becoming a clinical action. */
export const oversightThread = route<OversightThread>()('GET', `${A}/oversight/thread`, { honest: true });
/** POST /oversight/modeled/tell-nurse {guard}: the charge nurse on shift hears it on the bell, sealed. `reason` says why nobody was told. */
export const oversightTellNurse = route<{ real: boolean; notified: number; reason?: string | null }, void, { guard: string; confirm: true }>()('POST', `${A}/oversight/modeled/tell-nurse`, { honest: true });

/** GET /oversight/channels: fleets and care channels (Governed Systems). */
export const oversightChannels = route<OversightChannels>()('GET', `${A}/oversight/channels`, { honest: true });

// ── Surveyor export (V6-17, v3/oversight/survey-export.ts) ──

export type SurveyPart = 'guardian' | 'ledger' | 'breakglass' | 'medpass' | 'staffing';
export interface SurveyRow { at: string | null; what: string; who: string | null; resident: string | null; detail: string | null; ref: string | null }
export interface SurveyPartOut {
  name: string;
  rows: SurveyRow[];
  count: number;
  truncated: boolean;
  /** "Not recorded in Aithora for these dates." when the part has no rows. */
  empty: string | null;
}
export interface SurveyExport {
  real: boolean;
  facility: { id: string; name: string };
  range: { from: string; to: string; label: string };
  generatedAt: string;
  generatedBy: string;
  /** Resident names only for the requester's own building; initials otherwise. */
  namesShown: boolean;
  parts: Partial<Record<SurveyPart, SurveyPartOut>>;
  /** The Truth Ledger part as the signed range export (verifiable on its own). */
  ledger: Omit<LedgerEvidenceExport, 'real'> | null;
  csv: string;
  pdfBase64: string;
  manifest: { sha256: string; parts: SurveyPart[]; exportedAt: string; [k: string]: unknown };
  manifestJws: string;
  ledgerRef: string;
  custodyId: string;
}
/** POST /exports/survey {facility?, range | from+to, parts}: Facility Admin (own building) and Super Admin (read only). */
export const surveyExport = route<SurveyExport, void, { facility?: string; range?: '7d' | '30d' | '90d' | 'survey'; from?: string; to?: string; parts: SurveyPart[] }>()(
  'POST', `${A}/exports/survey`, { honest: true });

/** POST /exports/survey/link: the building's Facility Admin only. `url` carries the token, shown once; the server keeps only its hash. */
export interface SurveyLinkMade {
  real: true;
  id: string;
  url: string;
  path: string;
  expiresAt: string;
  hours: number;
  parts: SurveyPart[];
  range: { from: string; to: string; label: string };
}
export const surveyLinkCreate = route<SurveyLinkMade, void, { range?: '7d' | '30d' | '90d' | 'survey'; from?: string; to?: string; parts: SurveyPart[]; hours?: number }>()(
  'POST', `${A}/exports/survey/link`, { honest: true });

/** One use of a link: a file served, or refused because the link had expired or was revoked. */
export interface SurveyLinkAccess { at: string | null; file: 'page' | 'pdf' | 'csv' | 'signature'; outcome: 'served' | 'expired' | 'revoked' | 'failed'; ip: string | null }
export interface SurveyLink {
  id: string;
  parts: SurveyPart[];
  range: { from: string | null; to: string | null; label: string };
  createdAt: string | null;
  createdBy: string | null;
  expiresAt: string | null;
  /** Files downloaded through the link. */
  downloads: number;
  /** The last 20 uses, newest first. */
  access: SurveyLinkAccess[];
}
/** GET /exports/survey/links: the building's live links (not expired, not revoked). */
export const surveyLinks = route<{ real: true; links: SurveyLink[] }, { facility?: string }>()('GET', `${A}/exports/survey/links`, { honest: true });
/** POST /exports/survey/links/:id/revoke: the building's Facility Admin ends a link now. */
export const surveyLinkRevoke = route<{ real: true; id: string; revoked: true; revokedAt: string | null }>()('POST', `${A}/exports/survey/links/:id/revoke`, { honest: true });

export const oversightApi = {
  surveyExport,
  surveyLinkCreate,
  surveyLinks,
  surveyLinkRevoke,
  oversightChannels,
  oversightEvents,
  oversightEventPath,
  oversightHeld,
  oversightHeldDecide,
  oversightHuman,
  oversightReviewers,
  oversightPatterns,
  oversightModeled,
  oversightFreezeContact,
  oversightAlertFamily,
  aiPauseState,
  aiPauseSet,
  ledgerVerify,
  ledgerProof,
  ledgerExport,
  ledgerCustody,
  oversightGuardianSignals,
  killSwitches,
  killActivate,
  killDeactivate,
  securityAudit,
  governedTenants,
  governedTenantCreate,
  governedTenantPause,
  governedTenantPromote,
  governedStatus,
  governedSettings,
  ledgerEntries,
  oversightGuards,
  oversightTraces,
  oversightThread,
  oversightTellNurse,
} as const;
